import { AUTH_ROUTE_PREFIXES } from './authSession';

const STORAGE_KEY = 'llp_post_login_return_path';
const MAX_LEN = 2000;

/**
 * Returns a safe in-app path+search for post-login redirect, or null.
 * Rejects open redirects and auth pages.
 */
export function sanitizeInternalReturnPath(value) {
  if (value == null || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_LEN) return null;
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;

  const q = trimmed.indexOf('?');
  const pathname = (q === -1 ? trimmed : trimmed.slice(0, q)).split('#')[0];
  const lowerPath = pathname.toLowerCase();

  for (const prefix of AUTH_ROUTE_PREFIXES) {
    if (lowerPath === prefix || lowerPath.startsWith(`${prefix}/`)) return null;
  }

  return trimmed;
}

export function rememberPostLoginReturnPath(pathnameAndSearch) {
  const safe = sanitizeInternalReturnPath(pathnameAndSearch);
  if (!safe) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, safe);
  } catch (e) {
    // ignore quota / private mode
  }
}

/** Reads ?returnUrl= from a location.search string and persists if valid. */
export function syncPostLoginReturnPathFromLocationSearch(search) {
  if (!search || typeof search !== 'string') return;
  const qs = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(qs);
  const raw = params.get('returnUrl');
  if (raw == null || raw === '') return;
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch (e) {
    return;
  }
  rememberPostLoginReturnPath(decoded);
}

export function consumePostLoginReturnPath() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (!v) return null;
    const safe = sanitizeInternalReturnPath(v);
    sessionStorage.removeItem(STORAGE_KEY);
    return safe;
  } catch (e) {
    return null;
  }
}

export function clearPostLoginReturnPath() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // ignore
  }
}

/**
 * Only capture a return URL for cold visits (deep link while logged out).
 * When the user already had an active session in this mount, do not stash the
 * current URL for the next login (voluntary logout / in-app session clear).
 */
export function shouldRememberPostLoginReturnPath(hadActiveSessionInMount) {
  return !hadActiveSessionInMount;
}
